/**
 * Theme Context
 *
 * Provides theme state (light/dark mode) throughout the app.
 * Persists theme preference to localStorage and applies data-theme
 * attribute to document root for CSS variable switching.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import type { ThemeMode } from '@eptai/eptai-app-components';

// ============================================================
// Types
// ============================================================

interface ThemeContextValue {
  /** Current theme mode */
  theme: ThemeMode;
  /** Set theme explicitly */
  setTheme: (theme: ThemeMode) => void;
  /** Toggle between light and dark */
  toggleTheme: () => void;
}

// ============================================================
// Constants
// ============================================================

const THEME_STORAGE_KEY = 'eptai_theme';
const DEFAULT_THEME: ThemeMode = 'light';

// ============================================================
// Context
// ============================================================

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ============================================================
// Provider
// ============================================================

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps): JSX.Element {
  // Initialize from localStorage or default to light
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'dark' || stored === 'light') {
        return stored;
      }
    } catch {
      // localStorage not available
    }
    return DEFAULT_THEME;
  });

  // Apply theme to document root whenever it changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);

    // Also update body class for Bootstrap compatibility
    if (theme === 'dark') {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }

    // Persist to localStorage
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // localStorage not available
    }
  }, [theme]);

  // Set theme explicitly
  const setTheme = useCallback((newTheme: ThemeMode) => {
    setThemeState(newTheme);
  }, []);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    setThemeState(current => (current === 'light' ? 'dark' : 'light'));
  }, []);

  // Memoize context value
  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      toggleTheme,
    }),
    [theme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// ============================================================
// Hook
// ============================================================

/**
 * Hook to access theme context.
 * Must be used within a ThemeProvider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { theme, toggleTheme } = useTheme();
 *
 *   return (
 *     <button onClick={toggleTheme}>
 *       Current theme: {theme}
 *     </button>
 *   );
 * }
 * ```
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}

export default ThemeContext;
